/**
 * Transient-infra retry wrapper for market-creation and add-outcome
 * orchestrators. Retries only when the tx-engine classifies the failure as
 * `auto_retry` or `auto_retry_after_delay` (429, relay/Helius down, network
 * timeout). Blockchain/validation failures are returned immediately.
 *
 * Backoff is always the local schedule (1s → 2s → 5s); relay rate-limit hints
 * are not honored — 60s would stall interactive create-market flows.
 *
 * SINGLE RETRY AUTHORITY: `executePipeline` performs exactly ONE relay POST per
 * call — it does not re-relay internally. All transient resubmission lives here
 * so the two layers never stack into a multiplicative retry blowup.
 *
 * IDEMPOTENCY CONTRACT: each retry rebuilds and re-submits a *fresh* transaction
 * (new blockhash → new signature). A relay timeout/rejection can fire AFTER the
 * relay already broadcast the first attempt, so a retry may double-submit. This
 * wrapper is therefore safe ONLY for instructions that are idempotent on-chain —
 * `create_market` / `prepare_outcomes` derive fixed PDAs, so a landed-then-
 * retried tx fails closed as an "already initialized" contract error rather than
 * executing twice. `claim_prize` qualifies for the same reason: it burns the
 * wallet's claim rights, so a landed-then-retried claim fails closed on the
 * burn instead of paying twice. Do NOT reuse this for other value-moving
 * instructions (trades, transfers) where a duplicate submission would
 * double-spend.
 *
 * Callers whose step has an on-chain landing proof pass `alreadyLanded`: it runs
 * after each transient failure and again after the backoff, and a `true` stops
 * the retries and returns that failure, so the caller's own chain check decides
 * the step instead of resending a transaction that already landed.
 *
 * Each attempt receives its own transaction trace ID so the lifecycle contract
 * retains exactly one terminal record per trace. Workflow metadata keeps the
 * market-creation trace that joins those attempts.
 */

import type { PublicKey } from '@solana/web3.js';
import {
  createTransactionTraceId,
  resolveTransactionTraceId,
} from '../tx-engine/diagnostics/lifecycle.js';
import type {
  SubmissionOptions,
  SubmissionResult,
  SubmissionRetryContext,
} from '../tx-engine/types.js';

export const SUBMISSION_MAX_RETRIES = 3;
export const SUBMISSION_RETRY_DELAYS_MS = [1_000, 2_000, 5_000] as const;

type SubmitTransactionFn = (
  buildResult: {
    instructions: Array<{
      programId: PublicKey;
      keys: Array<{ pubkey: PublicKey; isSigner: boolean; isWritable: boolean }>;
      data: Buffer;
    }>;
    signers: PublicKey[];
    computeUnits?: number;
  },
  options: SubmissionOptions,
) => Promise<SubmissionResult>;

export type FailedSubmissionResult = Extract<SubmissionResult, { ok: false }>;
export type EngineBuildResult = Parameters<SubmitTransactionFn>[0];

export function submissionRetryDelayMs(failedAttempt: number): number {
  const index = failedAttempt - 1;
  return SUBMISSION_RETRY_DELAYS_MS[index] ?? 5_000;
}

export function isTransientInfraFailure(result: FailedSubmissionResult): boolean {
  const recoveryType = result.error.recoveryAction.type;
  return recoveryType === 'auto_retry' || recoveryType === 'auto_retry_after_delay';
}

export async function retryTransientSubmission(args: {
  readonly submitTransaction: SubmitTransactionFn;
  readonly buildResult: EngineBuildResult;
  readonly options: SubmissionOptions;
  readonly retryContext: SubmissionRetryContext;
  readonly signal: AbortSignal | undefined;
  readonly throwIfAborted: (signal: AbortSignal | undefined) => void;
  readonly sleepOrAbort: (delayMs: number, signal: AbortSignal | undefined) => Promise<void>;
  readonly alreadyLanded?: () => Promise<boolean>;
  readonly onRetryScheduled?: (info: {
    readonly attempt: number;
    readonly delayMs: number;
    readonly result: FailedSubmissionResult;
  }) => void;
}): Promise<SubmissionResult> {
  const initialTraceId = resolveTransactionTraceId(args.options.traceId);
  for (let failedAttempts = 0; ; ) {
    args.throwIfAborted(args.signal);
    const traceId = failedAttempts === 0 ? initialTraceId : createTransactionTraceId();
    const options: SubmissionOptions = {
      ...args.options,
      traceId,
      attemptContext:
        failedAttempts === 0
          ? args.retryContext
          : {
              ...args.retryContext,
              initialTraceId,
              retryAttempt: failedAttempts,
            },
      ...(args.options.metadata === undefined
        ? {}
        : { metadata: { ...args.options.metadata, txTraceId: traceId } }),
    };
    const result = await args.submitTransaction(args.buildResult, options);
    if (result.ok) return result;
    if (!isTransientInfraFailure(result) || failedAttempts >= SUBMISSION_MAX_RETRIES) {
      return result;
    }
    if (await args.alreadyLanded?.()) return result;

    failedAttempts += 1;
    const delayMs = submissionRetryDelayMs(failedAttempts);
    args.onRetryScheduled?.({ attempt: failedAttempts, delayMs, result });
    await args.sleepOrAbort(delayMs, args.signal);
    if (await args.alreadyLanded?.()) return result;
  }
}
